import React, { useState } from "react";
import { FaEye, FaTimes, FaFile, FaUsers, FaCalendarAlt, FaUniversity, FaCheckCircle, FaClock, FaTimesCircle, FaTags, FaFilePdf, FaDownload, FaInfoCircle } from "react-icons/fa";
import CiteThis from "./CiteThis";
import api from "../utils/api";

export default function ViewResearchModal({ isOpen, onClose, research }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  if (!isOpen || !research) return null;

  const handleDownload = async () => {
    if (!research._id) return;
    setDownloading(true);
    setDownloadError("");
    try {
      const response = await api.get(`/research/download/${research._id}`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = research.pdfFilename || "research-document.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 401) {
        setDownloadError("Please log in to download this document.");
      } else if (err.response?.status === 403) {
        setDownloadError("You do not have permission to download this document.");
      } else {
        setDownloadError("Failed to download document. Please try again.");
      }
    } finally {
      setDownloading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle className="text-green-400" />;
      case 'pending':
        return <FaClock className="text-yellow-400" />;
      case 'rejected':
        return <FaTimesCircle className="text-red-400" />;
      default:
        return <FaClock className="text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-600';
      case 'pending':
        return 'bg-yellow-600';
      case 'rejected':
        return 'bg-red-600';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="relative bg-gray-900 p-8 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto animate-fade-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white text-2xl font-bold focus:outline-none group"
          aria-label="Close"
        >
          <FaTimes className="group-hover:scale-110 transition-transform" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FaEye className="text-blue-500 text-2xl" />
          <h2 className="text-2xl font-bold text-white">Research Details</h2>
        </div>

        <div className="space-y-6">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaFile className="text-blue-400" />
              Title
            </label>
            <p className="text-white bg-gray-800 p-3 rounded-lg border border-gray-600">{research.title || "No title available"}</p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaUsers className="text-blue-400" />
              Authors
            </label>
            <p className="text-white bg-gray-800 p-3 rounded-lg border border-gray-600">
              {research.authors && research.authors.length > 0 ? research.authors.join(", ") : "No authors listed"}
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaFile className="text-blue-400" />
              Abstract
            </label>
            <p className="text-white bg-gray-800 p-3 rounded-lg border border-gray-600 max-h-32 overflow-y-auto">
              {research.abstract || "No abstract available"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <FaCalendarAlt className="text-blue-400" />
                Year
              </label>
              <p className="text-white bg-gray-800 p-3 rounded-lg border border-gray-600">{research.year || "N/A"}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <FaUniversity className="text-blue-400" />
                Campus
              </label>
              <p className="text-white bg-gray-800 p-3 rounded-lg border border-gray-600">{research.campus || "N/A"}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <FaTags className="text-blue-400" />
                Department
              </label>
              <p className="text-white bg-gray-800 p-3 rounded-lg border border-gray-600">{research.department || "N/A"}</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                Status
                <div className="relative group">
                  <FaInfoCircle className="text-gray-500 cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    Current approval status
                  </div>
                </div>
              </label>
              <div className="flex items-center gap-2 bg-gray-800 p-3 rounded-lg border border-gray-600">
                {getStatusIcon(research.status)}
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(research.status)}`}>
                  {research.status}
                </span>
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <FaCalendarAlt className="text-blue-400" />
                Date Uploaded
              </label>
              <p className="text-white bg-gray-800 p-3 rounded-lg border border-gray-600">
                {research.dateUploaded ? new Date(research.dateUploaded).toLocaleDateString() : 
                 research.createdAt ? new Date(research.createdAt).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaTags className="text-blue-400" />
              Keywords
            </label>
            <div className="flex flex-wrap gap-2 bg-gray-800 p-3 rounded-lg border border-gray-600">
              {research.keywords && research.keywords.length > 0 ? (
                research.keywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="bg-blue-600 px-3 py-1 rounded-full text-xs font-medium"
                  >
                    {keyword}
                  </span>
                ))
              ) : (
                <span className="text-gray-400 text-sm">No keywords available</span>
              )}
            </div>
          </div>

          {research.pdfFilename && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <FaFilePdf className="text-red-400" />
                Document
              </label>
              <div className="flex items-center justify-between bg-gray-800 p-3 rounded-lg border border-gray-600">
                <span className="text-white flex items-center gap-2">
                  <FaFilePdf className="text-red-400" />
                  {research.pdfFilename}
                </span>
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={downloading}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
                >
                  <FaDownload />
                  {downloading ? "Downloading…" : "Download"}
                </button>
              </div>
              {downloadError && (
                <p className="text-red-400 text-sm mt-2">{downloadError}</p>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-700">
          <CiteThis research={research} theme="dark" />
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 flex items-center gap-2"
          >
            <FaTimes />
            Close
          </button>
        </div>
      </div>
    </div>
  );
} 